import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const whoopRecoveryLegendDefs: Record<string, string> = {
  'Recovery %': 'WHOOP recovery score (0-100%). Reflects how recovered your body is based on HRV, resting HR, and sleep.',
  'Sleep %': 'WHOOP sleep performance score. Percentage of sleep need achieved.',
};

const whoopHrvLegendDefs: Record<string, string> = {
  'HRV (ms)': 'Heart Rate Variability (RMSSD in ms). Higher HRV generally indicates better recovery and aerobic fitness.',
  'Resting HR': 'Resting heart rate in BPM. Lower resting HR typically indicates better cardiovascular fitness.',
};

const whoopStrainLegendDefs: Record<string, string> = {
  'Strain': 'WHOOP daily strain score (0-21). Measures cardiovascular load from all activities throughout the day.',
};

interface WhoopPoint {
  date: string;
  recovery_score: number | null;
  hrv_rmssd: number | null;
  resting_hr: number | null;
  sleep_score: number | null;
  sleep_efficiency: number | null;
  strain_score: number | null;
  respiratory_rate: number | null;
  spo2: number | null;
}

type WhoopRange = '30' | '90' | '180' | '365';

const rangeOptions: { value: WhoopRange; label: string }[] = [
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '180', label: '6mo' },
  { value: '365', label: '1yr' },
];

const formatDateTick = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    const day = d.getDate();
    const mon = d.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${mon}`;
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{label}</div>
      {payload.map((p: any) => (
        p.value != null && (
          <div key={p.name} style={{ color: p.stroke || '#333', marginBottom: 2 }}>
            {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
          </div>
        )
      ))}
    </div>
  );
};

const WhoopRecoveryChart: React.FC<{ data: WhoopPoint[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="analytics-chart-loading">Loading...</div>;
  if (data.length === 0) return <div className="analytics-chart-empty">No WHOOP data available</div>;

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data}>
        <defs>
          <linearGradient id="recoveryGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#374151" stopOpacity={0.12} />
            <stop offset="100%" stopColor="#374151" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={formatDateTick} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11, fill: '#999' }} domain={[0, 100]} />
        <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" formatter={legendTooltipFormatter(whoopRecoveryLegendDefs)} />
        <Area type="monotone" dataKey="recovery_score" stroke="none" fill="url(#recoveryGradient)" dot={false} legendType="none" />
        <Line type="monotone" dataKey="recovery_score" stroke="#374151" strokeWidth={2.5} dot={false} name="Recovery %" connectNulls />
        <Line type="monotone" dataKey="sleep_score" stroke="#6b7280" strokeWidth={1.5} dot={false} name="Sleep %" connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const WhoopHrvChart: React.FC<{ data: WhoopPoint[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="analytics-chart-loading">Loading...</div>;
  if (data.length === 0) return <div className="analytics-chart-empty">No WHOOP data available</div>;

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={formatDateTick} interval="preserveStartEnd" />
        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#999' }} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#999' }} />
        <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" formatter={legendTooltipFormatter(whoopHrvLegendDefs)} />
        <Line yAxisId="left" type="monotone" dataKey="hrv_rmssd" stroke="#374151" strokeWidth={2} dot={false} name="HRV (ms)" connectNulls />
        <Line yAxisId="right" type="monotone" dataKey="resting_hr" stroke="#9ca3af" strokeWidth={1.5} dot={false} name="Resting HR" connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const WhoopStrainChart: React.FC<{ data: WhoopPoint[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) return <div className="analytics-chart-loading">Loading...</div>;
  // Exclude today — strain accumulates throughout the day
  const today = new Date().toISOString().split('T')[0];
  const filteredData = data.filter(d => d.date !== today);
  if (filteredData.length === 0) return <div className="analytics-chart-empty">No WHOOP data available</div>;

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={filteredData}>
        <defs>
          <linearGradient id="strainGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6b7280" stopOpacity={0.12} />
            <stop offset="100%" stopColor="#6b7280" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={formatDateTick} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11, fill: '#999' }} domain={[0, 21]} />
        <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" formatter={legendTooltipFormatter(whoopStrainLegendDefs)} />
        <Area type="monotone" dataKey="strain_score" stroke="none" fill="url(#strainGradient)" dot={false} legendType="none" />
        <Line type="monotone" dataKey="strain_score" stroke="#6b7280" strokeWidth={2} dot={false} name="Strain" connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

interface WhoopTrendsChartProps {
  targetUserId?: number | null;
}

const WhoopTrendsChart: React.FC<WhoopTrendsChartProps> = ({ targetUserId }) => {
  const [range, setRange] = useState<WhoopRange>('30');
  const [data, setData] = useState<WhoopPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<boolean | null>(null);

  const fetchData = useCallback(async (days: WhoopRange) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/web/whoop/trends?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 400) setConnected(false);
        throw new Error('Failed');
      }
      const json = await res.json();
      setData(json.data || []);
      setConnected(true);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    const params = targetUserId ? `?target_user_id=${targetUserId}` : '';
    fetch(`${API_BASE_URL}/api/web/whoop/status${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setConnected(d.connected);
        if (d.connected) fetchData(range);
        else setLoading(false);
      })
      .catch(() => { setConnected(false); setLoading(false); });
  }, [targetUserId]);

  useEffect(() => {
    if (connected) fetchData(range);
  }, [range, connected, fetchData]);

  if (connected === false) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div className="analytics-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>WHOOP Recovery & Sleep</h3>
          <div className="analytics-range-buttons" style={{ marginBottom: 0 }}>
            {rangeOptions.map(opt => (
              <button
                key={opt.value}
                className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
                onClick={() => setRange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <WhoopRecoveryChart data={data} loading={loading} />
      </div>

      <div className="analytics-chart-grid" style={{ marginTop: 20 }}>
        <div className="analytics-card">
          <h3>HRV & Resting Heart Rate</h3>
          <WhoopHrvChart data={data} loading={loading} />
        </div>
        <div className="analytics-card">
          <h3>Daily Strain</h3>
          <WhoopStrainChart data={data} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default WhoopTrendsChart;
